import { DrawSchedule, Lender, LenderContact } from '@/models/bank-models.model';
import { ParsedDraw } from '@/models/master-json.model';
import { BinderService } from '@/services/api/binder.service';
import { DrawsService } from '@/services/api/draws.service';
import { LendersService } from '@/services/api/lenders.service';
import { AppStateService } from '@/services/app/app-state.service';
import { FIDService } from '@/services/app/fid.service';
import { CommonModule } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DocDataService } from 'src/app/services/app/doc-data.service';
import { UserAccessService } from 'src/app/services/app/user-access.service';

@Component({
    selector: 'app-binder-financing',
    imports: [SkeletonModule, SelectModule, CommonModule, FormsModule, TagModule, TableModule],
    template: `
<div class="card flex flex-col gap-4">

<div class="text-2xl font-bold">Financing
  {{binderService.currentBinder().Franchises_Binders_SelectedFIDToFranchises.Bank_FID}}</div>
<div class="flex flex-wrap gap-4">
  <div class="flex flex-col grow basis-0 gap-2">
    <label>Lender</label>
    <p-select id="lender" [options]="lendersDataService.filteredLenders()" [(ngModel)]="binderService.currentBinder().LenderID"
      optionLabel="Title" optionValue="LenderID" placeholder="Select a Lender"
      (onChange)="onLenderChange($event.value)" [filter]="true" filterBy="Title"
      [disabled]="!userAccessService.binderEditAccess()"></p-select>
  </div>
  <div class="flex flex-col grow basis-0 gap-2">
    <label>Draw Schedule</label>
    <p-select [options]="drawsDataService.filteredSchedules()" [(ngModel)]="binderService.currentBinder().ScheduleID"
      optionLabel="Title" optionValue="ScheduleID" placeholder="Select a Draw Schedule"
      (onChange)="onDrawChange($event.value)" [filter]="true" filterBy="Title"
      [disabled]="!userAccessService.binderEditAccess()">
      <ng-template pTemplate="selectedItem" let-selectedOption>
        <div class="flex align-items-center justify-content-between w-full" *ngIf="selectedOption">
          <div>{{ selectedOption.Title }}</div>
          <p-tag severity="success" [value]="getScheduleType(selectedOption.FID)"
            [severity]="getScheduleTypeSeverity(selectedOption.FID)" />
        </div>
      </ng-template>
      <ng-template let-schedule pTemplate="item">
        <div class="flex align-items-center justify-content-between w-full">
          <div>{{ schedule.Title }}</div>
          <p-tag severity="success" [value]="getScheduleType(schedule.FID)"
            [severity]="getScheduleTypeSeverity(schedule.FID)" />
        </div>
      </ng-template>
    </p-select>
  </div>
</div>
<div class="text-900 w-full flex-order-1"
  *ngIf="docDataService.jsonData()?.financialValues && docDataService.jsonData()?.financialValues?.draws">
  <p-table [value]="docDataService.jsonData()?.financialValues?.draws ?? []" class="drawTable">
    <ng-template pTemplate="header">
      <tr>
        <th>%</th>
        <th>Event</th>
        <th>Amount</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-draw>
      <tr>
        <td>{{ draw.Percentage }}</td>
        <td id="drawEvent">{{ draw.Event }}</td>
        <td>
          <div *ngIf="binderService.totalsData() && binderService.totalsData().dispersalBalance">{{
            draw.amount |
            currency }}</div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>
</div>
    `,
    styles: `
    p-datatable {
    min-width: 20rem;
    font-size: smaller;
    padding: 0px;
    margin: 0px;
}

#drawEvent {
    font-size: 10px;;
}

p-datatable td, p-datatable th {
    padding: 1px !important;
}
    `
})
export class BinderFinancingComponent {
  filteredSchedules: DrawSchedule[] = [];
  draws: ParsedDraw[] = [];

  public binderService = inject(BinderService);
  public userAccessService = inject(UserAccessService);
  public lendersDataService = inject(LendersService);
  public drawsDataService = inject(DrawsService);
  public docDataService = inject(DocDataService);
  private appStateService = inject(AppStateService);
  private fidService = inject(FIDService);

  constructor() {
    if(
      this.lendersDataService.lenders().length === 0
    ) {
      this.lendersDataService.loadLenders();
    };
    if(this.drawsDataService.drawData().length === 0) {
      this.drawsDataService.loadDraws();
    }
    effect(() => {
      const json = this.docDataService.jsonData();
      if(json && json.financialValues.draws) {
        this.draws = json.financialValues.draws;
      }
    });

  }

  onDrawChange(scheduleId: number) {
    if(!this.appStateService.learningMode()) {
    const binder = {
      ScheduleID: scheduleId
    }
    const schedule = this.drawsDataService.filteredSchedules().find(s => s.ScheduleID === scheduleId);

    this.binderService.updateBinder(this.binderService.currentBinder().BinderID,binder).subscribe((updatedBinder) => {
      let binderData = updatedBinder;
      binderData.Schedules = schedule;
      this.binderService.patchBinder(binderData);
      });
    }
  }
  
  onLenderChange(lenderId: number) {
    if(!this.appStateService.learningMode()) {
    const binder = {
      LenderID: lenderId
    }
    this.binderService.updateBinder(this.binderService.currentBinder().BinderID,binder).subscribe((updatedBinder) => {
        this.binderService.patchBinder(updatedBinder);
      });
    }
  }

  filterAndSortLenders(lenders: Lender[], bank_fid: number | undefined | null, selectedFid: number) {
    console.log('lenders to filter:', lenders);
    console.log('lenders length:', lenders.length);
    if(selectedFid > 0) {
      let fid: number = this.fidService.determineBankFid(bank_fid, selectedFid);
      const filteredLenders = lenders.filter(lender => lender.LenderContacts && lender.LenderContacts.some((contact: LenderContact) => contact.FID === fid) || lender.isCash).sort((a, b) => {
        if (a.isCash !== b.isCash) {
          return b.isCash ? 1 : -1;  // Sort true values first
        }
        return a.Title.toLowerCase().localeCompare(b.Title.toLowerCase());  // Case-insensitive title comparison
      });
      console.log('filtered lenders:', filteredLenders);
      console.log('filtered lenders length:', filteredLenders.length);
      return filteredLenders;
    }
    return [];
  }

  getScheduleTitle(scheduleId: number | undefined | null) {
    if(scheduleId) {
      const schedule = this.filteredSchedules.find(s => s.ScheduleID === scheduleId);
      return schedule ? schedule.Title : '';
    }
    return '';
  }

  getScheduleType(fid: number | undefined | null) {
      if(fid === 5) {
          return 'Corp';
        }
      else {
        return 'Office';
      }
  }

  getScheduleTypeSeverity(fid: number | undefined | null)  {
      if(fid === 5) {
          return 'info';
        }
      else {
        return 'success';
      }
  }

  filterAndSortSchedules(schedules: DrawSchedule[], bank_fid: number | undefined | null, selectedFid: number) {
    console.log('schedules to filter:', schedules)
    console.log('bank_fid:', bank_fid)
    if(selectedFid > 0) {
      let fid: number = this.fidService.determineBankFid(bank_fid, selectedFid);
      console.log('fid to filter by:', fid)
      const filteredSchedules = schedules.filter(schedule => schedule.FID === fid || schedule.FID === 5);
      console.log('filtered schedules:', filteredSchedules)
      return filteredSchedules;
    }
    return [];
  }
}
